// Messaging service - persists messages via backend API
import SessionManager from '../utils/sessionManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

// Build headers with JWT Authorization (matches api.js pattern)
const buildHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// Get all messages for a conversation
export const getMessages = async (applicationId) => {
  try {
    const response = await fetch(`${API_URL}/messages/conversation/${applicationId}`, {
      headers: buildHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch messages');
    return await response.json();
  } catch (error) {
    console.error('Error loading messages:', error);
    return [];
  }
};

// Send a message
export const sendMessage = async (applicationId, sender, text, senderName = null) => {
  try {
    const response = await fetch(`${API_URL}/messages/send`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({
        application_id: applicationId,
        sender,
        sender_name: senderName || sender,
        text,
      }),
    });
    if (!response.ok) throw new Error('Failed to send message');
    const msg = await response.json();

    // Trigger custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('messagesUpdated', {
      detail: { applicationId, sender }
    }));

    return msg;
  } catch (error) {
    console.error('Error sending message:', error);
    return null;
  }
};

// Initialize conversation with welcome message if no messages exist
export const initializeConversation = async (applicationId, candidateName, jobTitle, companyName) => {
  try {
    const messages = await getMessages(applicationId);
    if (messages.length === 0) {
      await sendMessage(
        applicationId,
        'HR',
        `Hello ${candidateName}! Thank you for applying for the ${jobTitle} position at ${companyName}. We have received your application and will review it shortly. Feel free to reach out if you have any questions!`,
        `${companyName} HR Team`
      );
    }
  } catch (error) {
    console.error('Error initializing conversation:', error);
  }
};

// Get all conversations with metadata
export const getConversations = async () => {
  try {
    const response = await fetch(`${API_URL}/messages/conversations`, {
      headers: buildHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch conversations');
    return await response.json();
  } catch (error) {
    console.error('Error loading conversations:', error);
    return [];
  }
};

// Mark messages as read
export const markMessagesAsRead = async (applicationId, sender) => {
  try {
    await fetch(`${API_URL}/messages/mark-read`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify({ application_id: applicationId, sender }),
    });
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

// Get unread count for a specific application
export const getUnreadCount = async (applicationId, forSender) => {
  try {
    const messages = await getMessages(applicationId);
    return messages.filter(msg => msg.sender !== forSender && !msg.read).length;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

// Get total unread for HR
export const getTotalUnreadForHR = async () => {
  try {
    const response = await fetch(`${API_URL}/messages/unread-count`, {
      headers: buildHeaders(),
    });
    if (!response.ok) return 0;
    const data = await response.json();
    return data.unread_count || 0;
  } catch (error) {
    console.error('Error getting total unread for HR:', error);
    return 0;
  }
};

// Get total unread count (legacy compat)
export const getTotalUnreadCount = async (candidateId, forSender) => {
  return getTotalUnreadForHR();
};

// Get applications with unread messages
export const getApplicationsWithUnreadMessages = async () => {
  try {
    const convos = await getConversations();
    return convos
      .filter(c => c.unread_count > 0)
      .map(c => ({
        applicationId: c.application_id,
        unreadCount: c.unread_count,
        lastMessage: { text: c.last_message, sender: c.last_message_sender },
      }));
  } catch (error) {
    console.error('Error getting applications with unread messages:', error);
    return [];
  }
};

// Delete a conversation
export const deleteConversation = async (applicationId) => {
  try {
    await fetch(`${API_URL}/messages/conversation/${applicationId}`, {
      method: 'DELETE',
      headers: buildHeaders(),
    });
    window.dispatchEvent(new CustomEvent('messagesUpdated', {
      detail: { applicationId, deleted: true }
    }));
  } catch (error) {
    console.error('Error deleting conversation:', error);
  }
};

// Alias
export const deleteMessages = deleteConversation;
